import { faFileLines, faFileInvoice } from '@fortawesome/pro-light-svg-icons'
import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { ButtonSecondary } from '../../../../../../../../../../../baseComponents/Button'
import { ModalBigCentered } from '../../../../../../../../../../../baseComponents/Modal'
import { ModalOpenedSetTy } from '../../../../../../../../../../../baseComponents/Modal/types'
import { InputTableLayoutData } from '../../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/types'
import { TableData } from '../../../../../../../../../../../baseComponents/TwinTable/types'
import TwinTrans from '../../../../../../../../../../../baseComponents/TwinTrans'
import twinFetchPost from '../../../../../../../../../../../utils/globals/data'
import useTwinTranslation from '../../../../../../../../../../../utils/hooks/useTwinTranslation'
import { changeErrorMessage } from '../../../../../../../../../../../utils/reducers/reduxDispatch'
import { OrderData } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/types'
import { parseSelectedTableForProcessing, SelectedTableForProcessingTy } from '../../../../../../functions'
import locations from '../../../../../../../../../../../utils/globals/locations'
import { ProcessButton } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtons'
import './modalProcessBuyOrder.sass'
import { checkBuyDeliveryNoteFromOrderAllOk, checkBuyInvoiceFromOrderAllOk } from '../../../../../../../../../../../utils/globals/buy/buy'
import useIsLoading from '../../../../../../../../../../../utils/hooks/useIsLoading'
import LoadingSpinner from '../../../../../../../../../../../baseComponents/LoaderDecider/LoadingSpinner'

interface ModalProcessBuyOrderProps extends ModalProcessBuyOrderLogicProps {
    getCompleteData: (inputtable?: TableData) => Promise<void>
    setOpened: ModalOpenedSetTy
}

const ModalProcessBuyOrder: React.FC<ModalProcessBuyOrderProps> = ({ setOpened, data, ...rest }) => {
    const { decideComponent, selectedProducts } = useModalProcessBuyOrderLogic({ data })
    const Component = decideComponent()
    return (
        <ModalBigCentered opened={true} setOpened={() => setOpened(null)} onClickOut={false} className='flex modal_process_buy_order'>
            <div className='flex flex-col modal_process_buy_order_container'>
                <Component {...{ setOpened, data, selectedProducts, ...rest }} />
                <ButtonSecondary onClick={() => setOpened(null)} className='w-100 ml-auto mt-30 regular14'>
                    <TwinTrans transKey='back'>Volver</TwinTrans>
                </ButtonSecondary>
            </div>
        </ModalBigCentered>
    )
}
interface ModalProcessBuyOrderLogicProps {
    data: OrderData
}

const useModalProcessBuyOrderLogic = ({ data }: ModalProcessBuyOrderLogicProps) => {
    const selectedProducts = useMemo(() => parseSelectedTableForProcessing(data), [data])

    const decideComponent = useCallback(() => {
        const mustCancel = selectedProducts.canceling.products.length && !selectedProducts.processing.products.length
        if (mustCancel) {
            return CancelBuyOrderProducts
        }
        return ProcessBuyOrder
    }, [selectedProducts])

    return { decideComponent, selectedProducts }
}

interface CancelBuyOrderProductsProps extends CancelBuyOrderProductsLogicProps {}

const CancelBuyOrderProducts: React.FC<CancelBuyOrderProductsProps> = ({ ...logic }) => {
    const { t } = useTwinTranslation()
    const { finalizeOrder } = useCancelBuyOrderProductsLogic({ ...logic })
    return (
        <div className='finish_order'>
            <h4 className='text-start'><TwinTrans transKey='cancelBuyOrderProductSelectedProducts'>Cancelar todos los productos seleccionados</TwinTrans></h4>
            <div className='flex flex-col gap-y-15 mt-20'>
                <div className='flex gap-20 flex-auto w-200 m-auto'>
                    <ProcessButton finalizeOrder={finalizeOrder} url='/api/app/order/buy/buyOrder/orderProduct/cancelProcessedBuyOrderProducts' navigateTo={''} text={t('cancelSellOrderProduct', 'Cancelar productos')} icon={faFileLines} />
                </div>
            </div>
        </div>
    )
}

interface CancelBuyOrderProductsLogicProps {
    setOpened: ModalOpenedSetTy
    data: OrderData
    getCompleteData: (inputtable?: TableData) => Promise<void>
    selectedProducts: SelectedTableForProcessingTy
}

const useCancelBuyOrderProductsLogic = ({ setOpened, data, getCompleteData, selectedProducts }: CancelBuyOrderProductsLogicProps) => {
    const finalizeOrder = useCallback(async (url: string) => {
        const response = await twinFetchPost(url, { OrderId: data?.header?.id, orderProductIds: selectedProducts.forSend.products })
        const responseJSON = await response.json()
        if (response.status === 200) {
            await getCompleteData()
        } else {
            changeErrorMessage(responseJSON.errors[0].msg)
        }
        setOpened(null)
    }, [setOpened, getCompleteData, selectedProducts, data])
    
    return { finalizeOrder }
}

interface ProcessBuyOrderProps extends ProcessBuyOrderLogicProps {}

const ProcessBuyOrder: React.FC<ProcessBuyOrderProps> = ({ ...logic }) => {
    const { t } = useTwinTranslation()
    const { finalizeOrder, messageDeliveryNote, messageInvoice, loading } = useProcessBuyOrderLogic({ ...logic })
    return (
        <div className='process_buy_order'>
            <h4 className='text-start'>{t('processOrderFromSelectedProducts', 'Procesar pedido a partir de los productos seleccionados')}</h4>
            <div className='flex flex-col gap-y-15 mt-20'>
                {loading ? <LoadingSpinner /> :
                    <div className='flex gap-20 flex-auto'>
                        <ProcessButton finalizeOrder={finalizeOrder} url='/api/app/order/buy/buyOrder/createBuyDeliveryNoteFromBuyProducts' navigateTo={locations.buyDeliveryNoteSingle} text={t('createDeliveryNote', 'Crear albarán')} icon={faFileLines} messageError={messageDeliveryNote} />
                        <ProcessButton finalizeOrder={finalizeOrder} url='/api/app/order/buy/buyOrder/createBuyInvoiceFromBuyProducts' navigateTo={locations.buyInvoiceSingle} text={t('createInvoice', 'Crear factura')} icon={faFileInvoice} messageError={messageInvoice} />
                    </div>
                }
            </div>
        </div>
    )
}

interface ProcessBuyOrderLogicProps {
    data: InputTableLayoutData
    setOpened: ModalOpenedSetTy
    getCompleteData?: (inputtable?: TableData) => Promise<void>
    selectedProducts: SelectedTableForProcessingTy
}

const useProcessBuyOrderLogic = ({ data, setOpened, getCompleteData, selectedProducts }: ProcessBuyOrderLogicProps) => {
    const navigate = useNavigate()
    const {t} = useTwinTranslation()
    const messageDeliveryNote = checkBuyDeliveryNoteFromOrderAllOk(data, t)
    const messageInvoice = checkBuyInvoiceFromOrderAllOk(data, t)
    const errorUniqueNumberNLine = t('errorWithTheUniqueNumberNLine', 'Existe un error con el número de serie de la línea')
    const errorQuantityUniqueNumberNLine = t('errorQuantityWithTheUniqueNumberNLine', 'Existe un error con la cantidad del número de serie de la línea')
    const duplicateUniqueNumber = t('alreadyExistsUniqueNumber', 'Ya existe un producto con el número de serie')
    const errorQuantityRequired = t('quantityIsRequired', 'Es necesario procesar productos con cantidad')
    const noWarehouses = t('noWarehousesInWorkingCenter', 'Este centro de trabajo no tiene ningún almacén asociado')
    const { startLoading, endLoading, loading } = useIsLoading()
    
    const finalizeOrder = useCallback(async (url: string, navigateUrl: string) => {
        startLoading?.()
        const response = await twinFetchPost(url, { selectedProducts: selectedProducts.forSend.products, OrderId: data?.header?.id })
        const responseJSON = await response.json()
        if (response.status === 200) {
            if(responseJSON !== false){
                navigate(navigateUrl + '/' + responseJSON.id)
            }else{
                getCompleteData?.()
            }
        } else {
            const error = responseJSON.errors[0].msg
            const splitError = error.split('-')
            if (splitError[0] === 'This serial number already exists') {
                changeErrorMessage(duplicateUniqueNumber + ' ' + splitError[1])
            } else if (splitError[0] === 'Serial Number is required') {
                changeErrorMessage(errorUniqueNumberNLine + ' ' + splitError[1])
            } else if (splitError[0] === 'This serial number has quantity problem') {
                changeErrorMessage(errorQuantityUniqueNumberNLine + ' ' + splitError[1])
            } else if (splitError[0] === 'Products are required'){
                changeErrorMessage(errorQuantityRequired)
            } else if (splitError[0] === 'noWarehouses') {
                changeErrorMessage(noWarehouses)
            } else {
                changeErrorMessage(error)
            }
        }
        setOpened(null)
        endLoading?.()
    }, [navigate, setOpened, duplicateUniqueNumber, errorQuantityUniqueNumberNLine, errorUniqueNumberNLine, errorQuantityRequired, getCompleteData, data, selectedProducts.forSend.products, noWarehouses, startLoading, endLoading])
    return { finalizeOrder, messageInvoice, messageDeliveryNote, loading }
}

export default ModalProcessBuyOrder